import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";

export default function RelationshipTrainPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id"));
  const simId = searchParams.get("sim");
  const focusFromQuery = searchParams.get("focus") || "";

  const [session, setSession] = useState(null);
  const [focuses, setFocuses] = useState([]);
  const [focusKey, setFocusKey] = useState(focusFromQuery);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);
  const startedRef = useRef(false);

  useSeo({
    title: "关系训练｜换一种方式表达",
    metaDescription:
      "在安全的练习里，AI 扮演伴侣，帮你练习把感受与需要说清楚。不是心理治疗。",
    path: "/ai/relationship-train",
  });

  useEffect(() => {
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent(
          `/ai/relationship-train${simId ? `?sim=${simId}` : ""}`
        )}`,
        { replace: true }
      );
    }
  }, [userId, navigate, simId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.messages, busy]);

  useEffect(() => {
    if (!userId || startedRef.current) return;
    startedRef.current = true;
    const boot = async () => {
      setLoading(true);
      try {
        const focusRes = await apiJson("/api/ai/relationship-train/focuses");
        if (focusRes.res.ok) setFocuses(focusRes.data.items || []);

        // 若带 sim，直接开始；否则先展示焦点选择
        if (simId || focusFromQuery) {
          await startTrain(focusFromQuery);
        }
      } catch (err) {
        setError(err.message || "加载失败");
      } finally {
        setLoading(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const startTrain = async (key = focusKey) => {
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/ai/relationship-train/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          simulation_id: simId ? Number(simId) : null,
          focus_key: key || "",
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "无法开始训练");
      setSession(data);
      setFocusKey(data.focus?.key || key || "");
    } catch (err) {
      setError(err.message || "开始失败");
      alert(err.message || "开始失败");
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || !session?.id || busy || session.status === "done") return;
    setInput("");
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/relationship-train/reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          training_id: session.id,
          message: text,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "发送失败");
      setSession(data);
    } catch (err) {
      alert(err.message || "发送失败");
    } finally {
      setBusy(false);
    }
  };

  const feedback = session?.feedback;
  const progress = session?.progress || { turn: 0, total: 4 };

  return (
    <div className="min-h-screen bg-[#fbf7f2] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-3xl space-y-5">
        <section className="rounded-3xl bg-gradient-to-br from-emerald-50 via-violet-50 to-amber-50 border border-white p-6 space-y-2">
          <p className="text-sm text-emerald-700 font-medium">关系训练</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">
            换一种方式，再说一次
          </h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            AI 会扮演伴侣。你练习把感受与需要说清楚——这是练习，不是真实关系判决。
          </p>
          <div className="flex flex-wrap gap-2 text-xs pt-1">
            <Link to="/ai/simulation" className="text-violet-700 hover:underline">
              关系模拟
            </Link>
            <span className="text-gray-300">·</span>
            <Link
              to="/ai/behavior-profile"
              className="text-amber-800 hover:underline"
            >
              关系行为画像
            </Link>
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {loading && !session && (
          <p className="text-sm text-gray-400">准备练习场…</p>
        )}

        {!session && !loading && (
          <section className="bg-white rounded-2xl border p-5 space-y-4">
            <h2 className="font-semibold text-gray-800">选择练习焦点</h2>
            <div className="space-y-2">
              {focuses.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFocusKey(f.key)}
                  className={`w-full text-left rounded-xl border px-4 py-3 ${
                    focusKey === f.key
                      ? "border-violet-400 ring-2 ring-violet-100"
                      : "border-gray-100"
                  }`}
                >
                  <p className="text-sm font-semibold text-gray-800">{f.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => startTrain(focusKey || "general")}
              className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? "开始中…" : "开始练习"}
            </button>
          </section>
        )}

        {session && (
          <section className="bg-white rounded-2xl border border-violet-100 flex flex-col min-h-[60vh]">
            <div className="px-4 py-3 border-b space-y-1">
              <p className="text-xs text-violet-500">
                焦点 · {session.focus?.title}
              </p>
              <p className="text-sm text-gray-600">{session.focus?.desc}</p>
              <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-2 py-1 inline-block">
                提示：{session.focus?.hint}
              </p>
              <p className="text-xs text-gray-400">
                进度 {progress.turn} / {progress.total}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {(session.messages || []).map((m, i) => (
                <div
                  key={`${i}-${m.content?.slice(0, 8)}`}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-violet-600 text-white"
                        : "bg-rose-50 text-rose-900 border border-rose-100"
                    }`}
                  >
                    {m.role !== "user" && (
                      <p className="text-[10px] opacity-70 mb-1">伴侣（练习角色）</p>
                    )}
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <p className="text-xs text-gray-400">对方正在回应…</p>
              )}
              <div ref={bottomRef} />
            </div>

            {session.status !== "done" ? (
              <form
                onSubmit={send}
                className="p-4 border-t flex gap-2 items-end"
              >
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value.slice(0, 800))}
                  rows={2}
                  placeholder="试着用「我感到…我希望…」回应"
                  className="flex-1 border rounded-xl px-3 py-2 text-sm resize-none"
                  disabled={busy}
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  className="px-4 py-2.5 rounded-full bg-violet-600 text-white text-sm disabled:opacity-50"
                >
                  发送
                </button>
              </form>
            ) : (
              feedback && (
                <div className="p-4 border-t space-y-3 bg-violet-50/40">
                  <h3 className="font-bold text-violet-900">练习反馈</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {feedback.summary}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="bg-white rounded-xl border p-3">
                      <p className="text-xs text-emerald-700 font-semibold mb-1">
                        做得好的地方
                      </p>
                      <ul className="text-xs text-gray-700 space-y-1">
                        {(feedback.strengths || []).map((s) => (
                          <li key={s}>· {s}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-xl border p-3">
                      <p className="text-xs text-amber-800 font-semibold mb-1">
                        还可以练的地方
                      </p>
                      <ul className="text-xs text-gray-700 space-y-1">
                        {(feedback.watches || []).map((s) => (
                          <li key={s}>· {s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border p-3 text-sm text-gray-700">
                    <p className="text-xs text-violet-500 mb-1">下一句可以这样试</p>
                    {feedback.try_next}
                  </div>
                  {feedback.one_liner && (
                    <p className="text-sm italic text-violet-800">
                      “{feedback.one_liner}”
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => startTrain(session.focus?.key)}
                      className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm"
                    >
                      再练一轮
                    </button>
                    <Link
                      to="/ai/simulation"
                      className="px-4 py-2 rounded-full border text-sm"
                    >
                      回关系模拟
                    </Link>
                    <Link
                      to="/ai/behavior-profile"
                      className="px-4 py-2 rounded-full border text-sm text-amber-800"
                    >
                      看行为画像
                    </Link>
                  </div>
                </div>
              )
            )}
          </section>
        )}

        <p className="text-[11px] text-gray-400 leading-relaxed">
          {session?.disclaimer ||
            "这是表达练习，不是心理治疗，也不代表真实伴侣一定会这样回应。"}
        </p>
      </main>
      <Footer />
    </div>
  );
}
